import assert from 'assert';
import produce from 'immer';
import {useCallback, useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import {performDocumentRename} from 'notebook/documents/PerformDocumentRename';
import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {STEP_NAME_PLACEHOLDER} from 'procedures/design/step-config/StepConfigConstants';
import {StepGroupUpdater} from 'procedures/design/step-config/StepConfigEditor';
import {collectAllStepNames, findStep} from 'procedures/design/step-config/stepManagementUtils';
import {Method, ProcedureStepGroup} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

function normalizeStepName(
  draftName: string,
  thisStepCid: string,
  stepGroups: Array<ProcedureStepGroup>
): string {
  const namesAlreadyInUse = collectAllStepNames(stepGroups, thisStepCid);
  return normalizeNameForUniqueness(draftName, namesAlreadyInUse, STEP_NAME_PLACEHOLDER);
}

interface DraftStateEditorSetup {
  method: Method;
  currentlyEditingStepCid: string | null;
  onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void;
}

export interface DraftStateEditorInfo {
  draftName: string;
  setDraftName: (draftName: string) => void;
  storeDraftName: (stepCid: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  handleEnterAndEscape: (e: ElementKeyboardEvent) => void;
}

/**
 * Manages the step name text editor(s). While the user has a text editor focused, we block
 * autosaves, since we don't want to persist a draft name. Once the user finishes editing, we
 * can unblock autosaves, and normalize the name to ensure uniqueness.
 *
 * Note that the user can edit step names in two places. Using this tool in both places ensures
 * that they are correctly synchronized.
 */
export default function useStepNameDraftStateEditor(params: DraftStateEditorSetup): DraftStateEditorInfo {
  const initialName =
    (params.currentlyEditingStepCid
      ? findStep(params.method.stepGroups, params.currentlyEditingStepCid)
      : null
    )?.name ?? '';

  const [draftName, setDraftName] = useState<string>(initialName);
  const [initialDraftName, setInitialDraftName] = useState<string>(initialName);
  const {setLegacyShouldPauseAutosave} = ProcedurePageContext.useContext();

  const onChangeSteps = params.onChangeSteps;
  const stepGroups = params.method.stepGroups;
  const currentStep = findStep(stepGroups, params.currentlyEditingStepCid ?? '');
  const currentStepName = currentStep?.name;

  const storeDraftName = useCallback(
    (stepCid: string): void => {
      const stepBeingEdited = findStep(stepGroups, stepCid);
      assert(stepBeingEdited != null, `Could not find step with cid ${stepCid}`);
      const previousDocumentName = stepBeingEdited.name;
      const newDocumentName = normalizeStepName(draftName, stepCid, stepGroups);

      onChangeSteps((stepGroups1) => {
        const {documentId} = findStep(stepGroups1, stepCid)!;
        if (!documentId) {
          return stepGroups1;
        }

        let updatedDocumentIds: Array<string> = [];
        if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
          // For steps in Procedures Config, we need to register the name override so that it will be applied on
          // future saves and refreshes.
          DocumentActions.registerNameOverride(documentId, newDocumentName);
          // First, rename the document in the store. This will fire change events that update all steps
          updatedDocumentIds = performDocumentRename(documentId, previousDocumentName, newDocumentName);
        }
        // Renaming the /step/ is separate from renaming the /document/. This call prepares the new steps,
        // with the new step name.
        stepGroups1 = produce(stepGroups1, (draftStepGroups) => {
          const stepToRename = findStep(draftStepGroups, stepCid);
          assert(stepToRename != null, `Could not find step with cid ${stepCid}`);
          stepToRename.name = newDocumentName;
        });

        const updateDocumentsFromStore: StepGroupUpdater = (stepGroups2) =>
          produce(stepGroups2, (draftStepGroups) => {
            for (const stepGroup of draftStepGroups) {
              for (const step of stepGroup.steps) {
                if (
                  step.documentId &&
                  updatedDocumentIds.includes(step.documentId) &&
                  DocumentStore.getDocument(step.documentId)
                ) {
                  step.documentVersionUpdate = DocumentStore.getDocumentVersionUpdate(step.documentId);
                }
              }
            }
          });

        // We defer letting the document subsystem install its DVUs until after we've done internal
        // management. This ensures that we don't have any collisions with the internal step changes,
        // which also install documentVersionUpdates. (E.g., if you rename a step in the sidebar, those
        // changes are overwritten if document-initiated changes are not deferred.)
        setTimeout(() => {
          onChangeSteps(updateDocumentsFromStore);
        }, 0);

        return stepGroups1;
      });

      setLegacyShouldPauseAutosave(false);
    },
    [onChangeSteps, stepGroups, setLegacyShouldPauseAutosave, draftName]
  );

  const abandonDraft = (): void => {
    setDraftName(initialDraftName);
  };

  const onFocus = (): void => {
    setLegacyShouldPauseAutosave(true);
  };
  const onBlur = (): void => {
    if (params.currentlyEditingStepCid != null) {
      storeDraftName(params.currentlyEditingStepCid);
    }
    setLegacyShouldPauseAutosave(false);
  };

  // Install this in the `onKeyDown` handler, for correct processing of ENTER and ESC
  const handleEnterAndEscape = (e: ElementKeyboardEvent): void => {
    const isEsc = isEscapeKey(e);
    if (isEnterKey(e) || isEsc) {
      if (isEsc) {
        abandonDraft();
      }
      e.preventDefault();

      // React will reuse the event object, so we cache the element we want to blur
      const el = e.currentTarget;
      setTimeout(() => {
        el.blur();
      });
    }
  };

  // When the selected step changes, update our draft state
  useEffect(() => {
    let newDraftName: string;
    if (params.currentlyEditingStepCid == null) {
      newDraftName = '';
    } else {
      newDraftName = currentStepName || '';
    }
    setDraftName(newDraftName);
    setInitialDraftName(newDraftName);
  }, [params.currentlyEditingStepCid, currentStepName]);

  // Cleanup on unmount. This would be disastrous.
  useEffect(
    () => () => {
      setLegacyShouldPauseAutosave(false);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    draftName,
    setDraftName,
    storeDraftName,
    onFocus,
    onBlur,
    handleEnterAndEscape,
  };
}
